/**
 * enginePreProcessJson_Merge_TemplateTraversal.ts - Main template traversal for EnginePreProcessJson
 * Pure ES module - works in both Node.js and browser via bundler
 */

import { JsonObject } from '../../../../Assembler/dist/app/json/jsonValue.js';
import { Logger } from '../../../../Arshu/dist/logger/logger.js';
import { GenerateHtmlJson } from '../../base/loader/generateHtmlJson.js';
import {
    getValue,
    getKeys,
    getKeysList
} from './enginePreProcessJson_Helper.js';
import { TemplateEntryInfoNode, TemplateInfoNode, TemplateOperation } from './enginePreProcessJson_Helper.js';
import { TemplateOperationKind } from './enginePreProcessJson_Helper.js';
import {
    mergeArrayBlock
} from './enginePreProcessJson_Array.js';
import {
    TYPE_HTML,
    TYPE_JSONKEY,
    TYPE_JSONKEY_SKIP,
    TYPE_PLACEHOLDER,
    TYPE_PLACEHOLDER_SKIP,
    TYPE_SLOTHOLDER,
    TYPE_SLOTHOLDER_SKIP,
    TYPE_ARRAYBLOCK,
    TYPE_ARRAYBLOCK_SKIP,
    TYPE_SLOT,
    TYPE_SLOTHTML,
    TYPE_CONDITIONAL,
    META_KEY,
    isTypePrefixedKey
} from './enginePreProcessJson_Merge_Constants.js';
import { processPlaceholderEntry } from './enginePreProcessJson_Merge_Placeholder.js';
import { processSlotholderEntry } from './enginePreProcessJson_Merge_Slotholder.js';
import { processJsonKey } from './enginePreProcessJson_Merge_JsonKey.js';
import { processConditionalBlock } from './enginePreProcessJson_Merge_Conditional.js';
import { mergeSlotComponent } from './enginePreProcessJson_Merge_Slot.js';
import { tryReturnStaticHtml } from './enginePreProcessJson_Merge_Metadata.js';
import type { PreProcessJsonMergeContext } from './enginePreProcessJson_Merge.js';
import type { EnginePreProcessJson, ILoaderPreProcessJson } from './enginePreProcessJson.js';

type PlainJsonObject = { [key: string]: unknown };

/**
 * Core template merging logic
 */
export function mergeTemplate(
    engine: EnginePreProcessJson,
    loader: ILoaderPreProcessJson,
    context: PreProcessJsonMergeContext,
    templateInfo: TemplateInfoNode,
    htmlJson: JsonObject,
    dataJson: JsonObject,
    operations: TemplateOperation[],
    genOperations: boolean,
    componentHtml: JsonObject | null,
    recursionDepth: number,
    flowStack: string[] | null = null,
    parentComponentSink: TemplateOperation[] | null = null,
    keyReplaceStage: TemplateOperation[] | null = null,
    keyMissingStage: TemplateOperation[] | null = null,
    parentSlotSink: TemplateOperation[] | null = null
): string {
    // Only contextJson is read directly here (forwarded to the exempt processJsonKey helper);
    // every other invariant travels to child helpers via the context object itself.
    const contextJson = context.contextJson;
    // Prevent infinite recursion
    const maxRecursionDepth = loader.maxRecursionDepth;
    // csharp shape: prefer placeholderName (preserves parent-reference case like 'Center')
    // over templateInfo.templateName (which may be camelCased by getComponentId fallback).
    const templateKey = `${templateInfo.templateAppSite}_${(templateInfo as TemplateInfoNode).placeholderName || templateInfo.templateName}`;
    if (recursionDepth > maxRecursionDepth) {
        const errorMsg = `[ERROR] Maximum recursion depth (${maxRecursionDepth}) exceeded for template ${templateKey}. Possible circular template reference.`;
        Logger.error(errorMsg, 'EnginePreProcessJson');
        return errorMsg;
    }

    if (htmlJson) {
        const staticResult = tryReturnStaticHtml(htmlJson);
        if (staticResult.success) {
            return staticResult.staticHtml;
        }
    }

    const result: string[] = [];

    // Process HtmlJson content recursively
    const content = htmlJson || {};

    Logger.flow(`MergeTemplate: enter templateKey=${templateKey} depth=${recursionDepth} keys=${htmlJson ? htmlJson.size : 0}`, "PreProcessJson.TemplateRecursion");

    // Multi-stage ops emission mirroring Normal HTML engine's MergeTemplateWithJson phases:
    // ArraySkip → ArrayIteration → KeySkip → KeyReplace → KeyMissing → ConditionalBlock → Slots → Components
    // KeyReplace+KeyMissing come from MergeDataJsonIntoHtmlJson via keyReplaceStage/keyMissingStage.
    // Normal runs all slotholders then all components, so slot ops always precede component ops
    // regardless of document order. Slot/Component ops route to parentSlotSink/parentComponentSink
    // when set (hoists conditional-body slots/components out to the outer template's phase).
    const arraySkipOps: TemplateOperation[] = [];
    const arrayIterOps: TemplateOperation[] = [];
    const keySkipOps: TemplateOperation[] = [];
    const conditionalOps: TemplateOperation[] = [];
    const ownsComponentSink = parentComponentSink === null;
    const componentPhaseOps: TemplateOperation[] = parentComponentSink ?? [];
    const ownsSlotSink = parentSlotSink === null;
    const slotPhaseOps: TemplateOperation[] = parentSlotSink ?? [];

    // Process keys in insertion order. When metadata exactly matches this HtmlJson payload,
    // use precomputed entry type/name while still reading values from HtmlJson by key.
    const metadataEntries = tryGetExecutionEntriesForHtmlJson(templateInfo, htmlJson);
    const keysArray = metadataEntries
        ? metadataEntries.map(entry => ({
            key: entry.htmlJsonKey,
            keyType: entryTypeToKeyType(entry.entryType),
            keyNameFromKey: entry.name
        }))
        : getKeysList(getKeys(content)).map(key => ({
            key,
            keyType: isTypePrefixedKey(key) && key.length > 0 ? key[0] : '\0',
            keyNameFromKey: GenerateHtmlJson.getKeyName(key)
        }));

    for (const item of keysArray) {
        const key = item.key;
        // Skip metadata
        if (key === META_KEY) continue;

        const value = getValue(content, key);

        const keyType = item.keyType;
        const keyNameFromKey = item.keyNameFromKey;

        if (keyType === TYPE_HTML || keyType === TYPE_SLOTHTML) {
            // Plain HTML content
            result.push(value as string);
        } else if (keyType === TYPE_PLACEHOLDER || keyType === TYPE_PLACEHOLDER_SKIP) {
            Logger.flow(`MergeTemplate: placeholder=${key}`, "PreProcessJson.TemplateRecursion");
            // Process placeholder
            const params = {
                key,
                keyType,
                keyNameFromKey,
                value,
                templateInfo,
                dataJson,
                operations: componentPhaseOps,
                genOperations,
                componentHtml,
                recursionDepth,
                flowStack
            };
            const { result: placeholderResult, continue: shouldContinue } = processPlaceholderEntry(engine, loader, context, params, mergeTemplate);
            result.push(placeholderResult);
            if (shouldContinue) continue;
        } else if (keyType === TYPE_SLOTHOLDER || keyType === TYPE_SLOTHOLDER_SKIP) {
            Logger.flow(`MergeTemplate: slot=${key}`, "PreProcessJson.TemplateRecursion");
            // Process slotholder
            const params = {
                key,
                keyType,
                keyNameFromKey,
                value,
                templateInfo,
                dataJson,
                operations: slotPhaseOps,
                genOperations,
                componentHtml,
                recursionDepth,
                flowStack
            };
            const { result: slotholderResult, continue: shouldContinue } = processSlotholderEntry(engine, loader, context, params, mergeSlotComponent);
            result.push(slotholderResult);
            if (shouldContinue) continue;
        } else if (keyType === TYPE_SLOT) {
            if (typeof value === 'string') {
                result.push(value);
            }
            continue;
        } else if (keyType === TYPE_JSONKEY || keyType === TYPE_JSONKEY_SKIP) {
            // SKIP variant emits KeySkip op (phase 3). Non-skip is silently substituted —
            // KeyReplace/KeyMissing come from MergeDataJsonIntoHtmlJson via keyReplaceStage.
            result.push(processJsonKey.call(engine, loader, keyType, keyNameFromKey, value, dataJson, contextJson, templateInfo, templateKey, genOperations ? keySkipOps : null));
        } else if (keyType === TYPE_ARRAYBLOCK) {
            const arrayKey = keyNameFromKey;
            const arrayBlockData = value instanceof JsonObject ? value : new JsonObject(value as PlainJsonObject);

            const arrayMerged = mergeArrayBlock(
                mergeTemplate,
                engine,
                loader,
                context,
                arrayBlockData,
                arrayKey,
                dataJson,
                templateInfo,
                arrayIterOps,
                genOperations,
                componentHtml,
                recursionDepth,
                flowStack
            );
            result.push(arrayMerged);
        } else if (keyType === TYPE_ARRAYBLOCK_SKIP) {
            const skipArrayKey = keyNameFromKey;

            if (operations && genOperations) {
                const opTemplateName = `${templateInfo.templateAppSite}_${templateInfo.templateName}`;
                arraySkipOps.push(new TemplateOperation({
                    templateName: opTemplateName,
                    kind: TemplateOperationKind.ArraySkip,
                    target: `{{!@${skipArrayKey}}}`,
                    replacedWith: `${templateInfo.templateName}_${skipArrayKey} (skipped)`,
                    operations: []
                }));
            }
            result.push(loader.defaultSkipJson);
        } else if (keyType === TYPE_CONDITIONAL) {
            result.push(processConditionalBlock(mergeTemplate, engine, loader, context, templateInfo, value, keyNameFromKey, dataJson, conditionalOps, genOperations, componentHtml, recursionDepth, flowStack, componentPhaseOps, slotPhaseOps));
        } else if (typeof value === 'object' && value !== null) {
            // Hoist nested-object slot/component ops to this level's phase lists.
            const nested = mergeTemplate(
                engine,
                loader,
                context,
                templateInfo,
                value instanceof JsonObject ? value : new JsonObject(value as PlainJsonObject),
                dataJson,
                operations,
                genOperations,
                componentHtml,
                recursionDepth + 1,
                flowStack,
                componentPhaseOps,
                null,
                null,
                slotPhaseOps
            );
            result.push(nested);
        } else {
            result.push(value as string);
        }
    }

    // Canonical emission order per Normal HTML engine:
    // ArraySkip → ArrayIteration → KeySkip → KeyReplace → KeyMissing → ConditionalBlock → Slots → Components
    if (operations) {
        operations.push(...arraySkipOps);
        operations.push(...orderArrayIterOpsByDataKey(arrayIterOps, dataJson));
        operations.push(...keySkipOps);
        if (keyReplaceStage) operations.push(...keyReplaceStage);
        if (keyMissingStage) operations.push(...keyMissingStage);
        operations.push(...conditionalOps);
        // Slots flush before components, mirroring Normal's MergeTemplateSlots-then-ReplaceTemplatePlaceholders.
        if (ownsSlotSink) operations.push(...slotPhaseOps);
        if (ownsComponentSink) operations.push(...componentPhaseOps);
    }

    return result.join('');
}

function tryGetExecutionEntriesForHtmlJson(templateInfo: TemplateInfoNode, htmlJson: JsonObject): TemplateEntryInfoNode[] | null {
    if (!templateInfo.entries || templateInfo.entries.length === 0) {
        return null;
    }

    let nonMetaCount = 0;
    for (const key of htmlJson.keys()) {
        if (key !== META_KEY) {
            nonMetaCount++;
        }
    }

    if (nonMetaCount !== templateInfo.entries.length) {
        return null;
    }

    for (const entry of templateInfo.entries) {
        if (!entry.htmlJsonKey || !htmlJson.has(entry.htmlJsonKey)) {
            return null;
        }
    }

    return templateInfo.entries;
}

function entryTypeToKeyType(entryType: string): string {
    switch (entryType) {
        case 'Html': return TYPE_HTML;
        case 'JsonKey': return TYPE_JSONKEY;
        case 'JsonKeySkip': return TYPE_JSONKEY_SKIP;
        case 'Placeholder': return TYPE_PLACEHOLDER;
        case 'PlaceholderSkip': return TYPE_PLACEHOLDER_SKIP;
        case 'Slotholder': return TYPE_SLOTHOLDER;
        case 'SlotholderSkip': return TYPE_SLOTHOLDER_SKIP;
        case 'ArrayBlock': return TYPE_ARRAYBLOCK;
        case 'ArrayBlockSkip': return TYPE_ARRAYBLOCK_SKIP;
        case 'SlotHtml': return TYPE_SLOTHTML;
        case 'Slot': return TYPE_SLOT;
        case 'Conditional': return TYPE_CONDITIONAL;
        default: return '\0';
    }
}

function orderArrayIterOpsByDataKey(arrayIterOps: TemplateOperation[], jsonData: JsonObject): TemplateOperation[] {
    if (arrayIterOps.length <= 1 || !jsonData || jsonData.size === 0) {
        return arrayIterOps;
    }

    const keyIndex = new Map<string, number>();
    let dataIndex = 0;
    for (const key of jsonData.keys()) {
        const lower = key.toLowerCase();
        if (!keyIndex.has(lower)) {
            keyIndex.set(lower, dataIndex);
        }
        dataIndex++;
    }

    return arrayIterOps
        .map((op, originalIndex) => {
            const arrayKey = extractArrayKeyName(op.target).toLowerCase();
            return {
                op,
                originalIndex,
                dataIndex: arrayKey && keyIndex.has(arrayKey) ? keyIndex.get(arrayKey)! : Number.MAX_SAFE_INTEGER
            };
        })
        .sort((a, b) => a.dataIndex !== b.dataIndex ? a.dataIndex - b.dataIndex : a.originalIndex - b.originalIndex)
        .map(item => item.op);
}

function extractArrayKeyName(target: string | null | undefined): string {
    if (!target) {
        return '';
    }
    let s = target.trim();
    if (s.startsWith('{{')) {
        s = s.substring(2);
    }
    if (s.endsWith('}}')) {
        s = s.substring(0, s.length - 2);
    }
    return s.replace(/^[!@#^/]+/, '').trim();
}
